import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import {
  IconButton,
  Button,
  Dialog,
  DialogContent,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BlockIcon from "@mui/icons-material/Block";
import SearchBox from "./SearchBox";
import "./AzureMapComponent.css";

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;

const GoogleMap = forwardRef(({
  pins,
  statusFilters,
  handleStatusFilterChangeButton,
  salesList,
  selectedSalesperson,
  setSelectedSalesperson,
  fetchData,
  // Isochrone props
  mergedIsochrones,
  updateIsochronePercentages,
  // Common utility functions from parent
  isTrue,
  generateImageId,
  getCRMLink,
  passesFilters,
  // New cluster config from parent
  clusterConfig
}, ref) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const polygonsRef = useRef([]);
  const markerClustererRef = useRef(null);
  const [selectedPin, setSelectedPin] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mapStyle, setMapStyle] = useState("hybrid");

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getCurrentBounds: () => {
      if (!mapRef.current) return null;
      const b = mapRef.current.getBounds();
      if (!b) return null;
      const ne = b.getNorthEast();
      const sw = b.getSouthWest();
      return [sw.lng(), sw.lat(), ne.lng(), ne.lat()];
    },
    getVisibleGreenPins: (bounds) => {
      if (!bounds) return [];
      const [w, s, e, n] = bounds;
      
      let visiblePins = pins.filter((p) => {
        const lat = parseFloat(p.Latitude);
        const lon = parseFloat(p.Longitude);
        return (
          !isNaN(lat) &&
          !isNaN(lon) &&
          lon >= w &&
          lon <= e &&
          lat >= s &&
          lat <= n
        );
      });

      const greenWonPins = visiblePins.filter(
        (p) =>
          (p.StatusReason === "100000000" || p.StatusReason === "5") &&
          p.Status === "0"
      );
      
      return greenWonPins
        .map((p) => ({
          lat: parseFloat(p.Latitude),
          lng: parseFloat(p.Longitude)
        }))
        .filter(({ lat, lng }) => !isNaN(lat) && !isNaN(lng));
    }
  }));

  /* ---------------- GOOGLE MARKERS ----------------- */
  const createMarkers = (data) => {
    if (!mapRef.current) return;
    
    // Clear existing markers and clusterer
    if (markerClustererRef.current) {
      markerClustererRef.current.clearMarkers();
      markerClustererRef.current.setMap(null);
      markerClustererRef.current = null;
    }
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const filteredData = data.filter(passesFilters);
    const markers = [];

    filteredData.forEach((pin) => {
      const lat = parseFloat(pin.Latitude);
      const lng = parseFloat(pin.Longitude);
      if (isNaN(lat) || isNaN(lng)) return;
      
      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        icon: {
          url: `/icons/${generateImageId(pin)}.png`,
          scaledSize: new window.google.maps.Size(32, 32)
        },
        map: null // Don't add to map directly
      });
      
      marker.addListener("click", () => {
        setSelectedPin(pin);
        setDialogOpen(true);
      });
      
      markers.push(marker);
      markersRef.current.push(marker);
    });

    // Create clusterer with proper settings
    if (clusterConfig.enabled && window.google && window.google.maps) {
      try {
        // Let's use the default Google clusters but with a customized renderer
        markerClustererRef.current = new window.MarkerClusterer(mapRef.current, markers, {
          gridSize: clusterConfig.radius || 60,
          minimumClusterSize: clusterConfig.minPoints || 2,
          maxZoom: 14,
          // Use default algorithm & clustering
          // The imageExtension is necessary if using the default path
          imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
          // Override the cluster renderer to create properly centered blue circles
          renderer: {
            render: function({ count, position }) {
              // Create a custom div for better centering
              const div = document.createElement('div');
              div.style.borderRadius = '50%';
              div.style.padding = '8px';
              div.style.color = 'white';
              div.style.fontWeight = 'bold';
              div.style.fontSize = '14px';
              div.style.textAlign = 'center';
              div.style.width = '40px';
              div.style.height = '40px';
              div.style.lineHeight = '24px'; // Center vertically
              div.style.backgroundColor = '#1E88E5'; // Blue color
              div.style.border = '2px solid white';
              div.style.display = 'flex';
              div.style.alignItems = 'center';
              div.style.justifyContent = 'center';
              div.textContent = count;
              
              // Create the marker
              return new window.google.maps.Marker({
                position: position,
                icon: {
                  url: 'data:image/svg+xml;charset=UTF-8,' + 
                       encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><circle cx="20" cy="20" r="18" fill="#1E88E5" stroke="white" stroke-width="2"/></svg>'),
                  size: new window.google.maps.Size(40, 40),
                  anchor: new window.google.maps.Point(20, 20),
                  labelOrigin: new window.google.maps.Point(20, 20)
                },
                label: {
                  text: String(count),
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold'
                },
                zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count
              });
            }
          }
        });
      } catch (e) {
        console.error("MarkerClusterer error:", e);
        // Fallback: add markers directly to map if clustering fails
        markers.forEach(marker => marker.setMap(mapRef.current));
      }
    } else {
      // If clustering is disabled, add markers directly to map
      markers.forEach(marker => marker.setMap(mapRef.current));
    }
  };

  /* ------------- SEARCH LOCATION CENTER ---------- */
  const handleLocationSelect = (pos, prop = null) => {
    if (mapRef.current) {
      mapRef.current.setCenter({ lat: pos.lat, lng: pos.lon });
      mapRef.current.setZoom(
        prop?.type === "street" ? 16 : prop?.type === "city" ? 10 : 10
      );
    }
  };

  /* ------------ CLEAR ISOCHRONE LAYERS ------------- */
  const clearIsochroneLayers = () => {
    polygonsRef.current.forEach((p) => p.setMap(null));
    polygonsRef.current = [];
  };

  /* ------------- ISO RENDER – GOOGLE ------------ */
  const renderIsochrones = () => {
    if (!mapRef.current || !mergedIsochrones) return;

    clearIsochroneLayers();

    const addPoly = (dur, color, z) => {
      const arr = mergedIsochrones[dur] || mergedIsochrones[Number(dur)];
      if (!arr?.length) return;
      arr.forEach((poly) => {
        const path = poly.map((pt) => ({
          lat: pt.Lat || pt.lat,
          lng: pt.Lng || pt.lng
        }));
        const gPoly = new window.google.maps.Polygon({
          paths: path,
          strokeColor: color,
          strokeOpacity: 1,
          strokeWeight: 2,
          fillColor: color,
          fillOpacity: 0.5,
          map: mapRef.current,
          zIndex: z
        });
        polygonsRef.current.push(gPoly);
      });
    };
    addPoly("20", "#db1713", 1);
    addPoly("15", "#f78b07", 2);
    addPoly("10", "#fbc62f", 3);
    addPoly("5", "#4caf50", 4);
    
    // Calculate initial percentages
    if (mapRef.current) {
      const bounds = ref.current.getCurrentBounds();
      updateIsochronePercentages(bounds);
    }
  };

  useEffect(() => {
    if (mergedIsochrones) {
      renderIsochrones();
    } else {
      clearIsochroneLayers();
    }
  }, [mergedIsochrones]);
  
  /* ------------ ATTACH MAP EVENTS FOR DYNAMIC UPDATES --------- */
  useEffect(() => {
    if (!mapRef.current || !mergedIsochrones) return;
    
    // Add listeners for map movements that should trigger recalculation
    const boundsChangedListener = mapRef.current.addListener(
      'bounds_changed',
      () => {
        if (window.googleMapsBoundsTimer) {
          clearTimeout(window.googleMapsBoundsTimer);
        }
        window.googleMapsBoundsTimer = setTimeout(() => {
          const bounds = ref.current.getCurrentBounds();
          if (bounds) updateIsochronePercentages(bounds);
        }, 100);
      }
    );
    
    // More reliable trigger after user interaction ends
    const idleListener = mapRef.current.addListener(
      'idle',
      () => {
        const bounds = ref.current.getCurrentBounds();
        if (bounds) updateIsochronePercentages(bounds);
      }
    );
    
    // Initial calculation
    const bounds = ref.current.getCurrentBounds();
    if (bounds) updateIsochronePercentages(bounds);
    
    return () => {
      if (boundsChangedListener) {
        window.google.maps.event.removeListener(boundsChangedListener);
      }
      if (idleListener) {
        window.google.maps.event.removeListener(idleListener);
      }
      if (window.googleMapsBoundsTimer) {
        clearTimeout(window.googleMapsBoundsTimer);
      }
    };
  }, [mergedIsochrones]);

  /* ------------------ GOOGLE MAP INIT ------------------ */
  useEffect(() => {
    if (!window.google) {
      const s = document.createElement("script");
      s.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places,visualization`;
      s.async = true;
      s.defer = true;
      s.onload = () => {
        // Load MarkerClusterer library after Google Maps is loaded
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/@google/markerclustererplus@4.0.1/dist/markerclustererplus.min.js';
        script.async = true;
        script.onload = initMap;
        document.head.appendChild(script);
      };
      document.head.appendChild(s);
    } else {
      // Check if MarkerClusterer needs to be loaded
      if (!window.MarkerClusterer) {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/@google/markerclustererplus@4.0.1/dist/markerclustererplus.min.js';
        script.async = true;
        script.onload = initMap;
        document.head.appendChild(script);
      } else {
        initMap();
      }
    }

    function initMap() {
      const gDiv = document.createElement("div");
      gDiv.id = "googleMapCanvas";
      gDiv.style.cssText =
        "position:absolute;top:0;left:0;width:100%;height:100%;";
      containerRef.current.appendChild(gDiv);

      mapRef.current = new window.google.maps.Map(gDiv, {
        center: { lat: 39.8283, lng: -98.5795 }, // San Francisco as default center
        zoom: 3.5, // Better default zoom level for city view
        mapId: "da37f3254c6a6d1c",
        mapTypeId: mapStyle,
        gestureHandling: 'greedy',
        clickableIcons: false,
        fullscreenControl: false,
        tilt: 10
      });

      mapRef.current.addListener("idle", () => {
        if (mergedIsochrones) {
          const bounds = ref.current.getCurrentBounds();
          updateIsochronePercentages(bounds);
        }
      });

      // Add an event listener for when zoom changes to handle clustering visibility
      mapRef.current.addListener("zoom_changed", () => {
        if (markerClustererRef.current) {
          // Optionally update clustering settings based on zoom
          const zoom = mapRef.current.getZoom();
          // Custom logic if needed based on zoom level
        }
      });

      createMarkers(pins);
      if (mergedIsochrones) renderIsochrones();
    }

    return () => {
      if (mapRef.current) {
        const gDiv = document.getElementById("googleMapCanvas");
        if (gDiv) gDiv.remove();
      }
    };
  }, []);
  

  /* --------- UPDATE PINS ON CHANGE ---------- */
  useEffect(() => {
    createMarkers(pins);
  }, [pins, statusFilters, selectedSalesperson, clusterConfig]);

  /* ------------ UPDATE MAP STYLE --------- */
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setMapTypeId(mapStyle);
    }
  }, [mapStyle]);

  /* ------------ WATERMARK/POPUP KILLER ---------- */
  useEffect(() => {
    const obs = new MutationObserver((muts) => {
      muts.forEach(({ addedNodes }) => {
        addedNodes.forEach((n) => {
          if (
            n.nodeType === 1 &&
            /This page can't load Google Maps correctly|Do you own this website\?/i.test(
              n.textContent || ""
            )
          ) {
            n.remove();
          }
        });
      });
    });
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  return (
    <div className="map-fullscreen-container" ref={containerRef}>
      {/* --- FILTERS ROW --- */}
      <div className="filters-overlay">
        <div className="filters-row">
          <div className="filters">
            <Button
              className={`filter-button green-filter ${
                statusFilters.Green ? "active" : ""
              }`}
              onClick={() => handleStatusFilterChangeButton("Green")}
              startIcon={
                <img
                  src="/icons/won-icon.png"
                  alt="Won"
                  className="icon"
                />
              }
            >
              Won
            </Button>
            <Button
              className={`filter-button yellow-filter ${
                statusFilters.Yellow ? "active" : ""
              }`}
              onClick={() => handleStatusFilterChangeButton("Yellow")}
              startIcon={
                <img
                  src="/icons/in-progress-icon.png"
                  alt="In Progress"
                  className="icon"
                />
              }
            >
              In Progress
            </Button>
            <Button
              className={`filter-button red-filter ${
                statusFilters.Red ? "active" : ""
              }`}
              onClick={() => handleStatusFilterChangeButton("Red")}
              startIcon={
                <img src="/icons/lost-icon.png" alt="Lost" className="icon" />
              }
            >
              Lost
            </Button>
            <Button
              className={`filter-button reit-filter ${
                statusFilters.REIT ? "active" : ""
              }`}
              onClick={() => handleStatusFilterChangeButton("REIT")}
              startIcon={
                <img
                  src="/icons/reit-icon.png"
                  alt="REIT"
                  className="icon"
                  style={{ width: 16, height: 16 }}
                />
              }
            >
              REIT
            </Button>
          </div>
          <div className="filters">
            <Button
              className={`filter-button garage-filter ${
                statusFilters.Garage ? "active" : ""
              }`}
              onClick={() => handleStatusFilterChangeButton("Garage")}
              startIcon={
                <img
                  src="/icons/garage-icon.png"
                  alt="Garage"
                  style={{ width: 18, height: 18 }}
                />
              }
            >
              Garage
            </Button>
            <Button
              className={`filter-button gated-filter ${
                statusFilters.GatedFenced ? "active" : ""
              }`}
              onClick={() => handleStatusFilterChangeButton("GatedFenced")}
              startIcon={
                <img
                  src="/icons/fence-icon.png"
                  alt="Gated/Fenced"
                  style={{ width: 16, height: 14 }}
                />
              }
            >
              Gated/Fenced
            </Button>
            <Button
              className={`filter-button fee-filter ${
                statusFilters.Fee ? "active" : ""
              }`}
              onClick={() => handleStatusFilterChangeButton("Fee")}
              startIcon={<AttachMoneyIcon className="icon" />}
            >
              Fee
            </Button>
            {statusFilters.Green && (
              <Button
                className={`filter-button inactive-filter ${
                  statusFilters.NotRegistered ? "active" : ""
                }`}
                onClick={() =>
                  handleStatusFilterChangeButton("NotRegistered")
                }
                startIcon={<BlockIcon className="icon" />}
              >
                Not Registered
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* --- SEARCH BOX --- */}
      <div className="search-overlay">
        <SearchBox onLocationSelect={handleLocationSelect} />
      </div>

      {/* --- CLUSTER INFO TOOLTIP --- */}
      <div className="cluster-info" style={{ 
        position: 'absolute', 
        bottom: '40px', 
        right: '10px', 
        backgroundColor: 'rgba(255,255,255,0.8)',
        padding: '5px 10px',
        borderRadius: '4px',
        fontSize: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        zIndex: 1000
      }}>
        <div>Click on clusters to zoom in and view individual properties</div>
      </div>

      {/* --- MAP STYLE TOGGLE --- */}
      <div
        style={{
          position: "fixed",
          top: 180,
          right: "2%",
          zIndex: 4000,
          background: "rgba(255,255,255,0.9)",
          borderRadius: 8,
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          padding: 6,
          display: "flex",
          gap: 6
        }}
      >
        {[
          ["roadmap", "🗺️", "Road"],
          ["satellite", "🛰️", "Sat"],
          ["hybrid", "🌐", "Hybrid"],
          ["terrain", "⛰️", "Terrain"]
        ].map(([id, icon, label]) => (
          <button
            key={id}
            onClick={() => setMapStyle(id)}
            title={label}
            style={{
              border: "none",
              background: id === mapStyle ? "#0078D4" : "transparent",
              cursor: "pointer",
              fontSize: 18,
              padding: "4px 6px",
              borderRadius: 4
            }}
          >
            {icon}
          </button>
        ))}
      </div>

      {/* --- ZOOM CONTROLS --- */}
      <div
        style={{
          position: "fixed",
          top: 380,
          right: "2%",
          zIndex: 4000,
          background: "rgba(255,255,255,0.9)",
          borderRadius: 8,
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          padding: 6,
          display: "flex",
          flexDirection: "column",
          gap: 6
        }}
      >
        <button
          onClick={() => mapRef.current?.setZoom((mapRef.current?.getZoom() || 0) + 1)}
          title="Zoom In"
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: 18,
            padding: "4px 6px",
            borderRadius: 4
          }}
        >
          ➕
        </button>
        <button
          onClick={() => mapRef.current?.setZoom((mapRef.current?.getZoom() || 0) - 1)}
          title="Zoom Out"
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: 18,
            padding: "4px 6px",
            borderRadius: 4
          }}
        >
          ➖
        </button>
      </div>

      {/* -------- PROPERTY DIALOG -------- */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            overflow: "hidden",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            margin: 0,
            padding: "8px 16px",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)"
          }
        }}
        BackdropProps={{ style: { backgroundColor: "transparent" } }}
      >
        <DialogContent style={{ display: "flex", padding: 0 }}>
          {selectedPin && (
            <>
              <div style={{ width: "40%", position: "relative", height: "80%" }}>
                <img
                  src="/icons/robo-icon.png"
                  alt="Robotaxi"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </div>
              <div
                style={{
                  width: "60%",
                  padding: 16,
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center"
                }}
              >
                <IconButton
                  onClick={() => setDialogOpen(false)}
                  style={{ position: "absolute", top: -5, right: -5 }}
                >
                  <CloseIcon />
                </IconButton>
                <Typography
                  variant="h6"
                  style={{ fontWeight: "bold", fontSize: "1.2rem", lineHeight: 1.6 }}
                >
                  {`${selectedPin.PropertyAddress},${selectedPin.City},${selectedPin.State} ${selectedPin.Zip}`}
                </Typography>
                {/* <Typography
                  variant="body2"
                  style={{ fontSize: "1rem", color: "#555", lineHeight: 1.6 }}
                  gutterBottom
                >
                  Number of Parking Spaces:{" "}
                 {Array.isArray(selectedPin?.coordinateDetails)
                ? selectedPin.coordinateDetails.filter(
                (coord) => coord.title === "PSL - Parking Space Location"
                ).length
                : "N/A"}
                </Typography> */}
                <Typography 
                 variant="body2"
                 style={{ fontSize: "1rem", color: "#555", lineHeight: 1.6 }}
                 gutterBottom
                 >
                 Number of Parking Spaces:{" "}
                 {selectedPin?.NumberOfParkingSpaces?.toString().trim() || "N/A"}
                </Typography>
                 <Typography
                                  variant="body2" style={{ fontSize: "1rem", color: "#555", lineHeight: 1.6 }}
                                  gutterBottom
                                >
                                Status: {(() => {const raw = selectedPin?.StatusReason?.toString?.() ?? "unknown";
                                const statusMap = {"100000000": "Won","1": "In Progress","2": "Lost","5": "Won","unknown": "Unknown"};
                                return statusMap[raw] || `Unknown (${raw})`;
                                })()}
                                </Typography>
               <Typography variant="body2" style={{ fontSize: "1rem", color: "#555", lineHeight: 1.6, display: "flex", alignItems: "center", gap: "0.5rem" }}>
                               {[
                               selectedPin?.REIT === "True" ? <img src="/icons/reit-icon.png" alt="REIT" style={{ width: 16, height: 16 }} /> : null,
                               selectedPin?.garage === "True" ? <img src="/icons/garage-icon.png" alt="REIT" style={{ width: 16, height: 16 }} /> : null,
                               selectedPin?.GatedOrFenced === "True" ? <img src="/icons/fence-icon.png" alt="REIT" style={{ width: 16, height: 16 }} /> : null,
                               selectedPin?.Fee === "True" ? (<AttachMoneyIcon style={{ fontSize: 16 }} />) : null,].filter(Boolean).map((item, index) => (
                               <span key={index}>{item}</span>
                               )) || "N/A"}
                               </Typography>
                {selectedPin.Id && (
                  <Typography
                    variant="body2"
                    style={{
                      marginTop: 16,
                      color: "#0078D4",
                      cursor: "pointer",
                      fontSize: "1rem"
                    }}
                  >
                    <a
                      href={getCRMLink(selectedPin.Id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: "none",
                        color: "#0078D4",
                        fontWeight: "bolder",
                        fontSize: 15
                      }}
                    >
                      Click here to launch the CRM opportunity: <u>Link</u>
                    </a>
                  </Typography>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default GoogleMap;